import { Button } from "components/DesignSystem/Button/Button";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import { DateInput } from "components/DesignSystem/DateInput/DateInput";
import Modal from "components/DesignSystem/Modal/Modal";
import { TextArea } from "components/DesignSystem/TextArea/TextArea";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { FormikForm } from "components/FormikForm/FormikForm";
import { FileInput } from "components/FileInput/FileInput";
import { PriceInput } from "components/PriceInput/PriceInput";
import { useToast } from "hooks/useToast";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useMemo } from "react";
import { Field, FieldProps, FormikValues } from "formik";
import { ModalProps } from "types/utils/modal";
import {
  useLogIRSPaymentMutation,
  useUpdateIRSPaymentMutation,
} from "store/apis/estimatedTax";
import { useUploadFileMutation } from "store/apis/documents";
import { IRSPayment } from "types/Models/estimatedTax";
import { BackendError } from "types/utils/error";
import { getSeasonsFrom } from "utils/getAllSeason";
import dayjs from "dayjs";
import * as Yup from "yup";

const PAYMENT_METHOD_OPTIONS = [
  { value: "EFTPS", label: "EFTPS" },
  { value: "DIRECT_PAY", label: "Direct Pay" },
  { value: "CHECK", label: "Check" },
  { value: "CREDIT_CARD", label: "Credit Card" },
  { value: "OTHER", label: "Other" },
];

const QUARTER_OPTIONS = [
  { value: "Q1", label: "Q1" },
  { value: "Q2", label: "Q2" },
  { value: "Q3", label: "Q3" },
  { value: "Q4", label: "Q4" },
];

const currentYear = dayjs().year();
const YEAR_OPTIONS = getSeasonsFrom(2020, currentYear + 1)
  .reverse()
  .map((y) => ({ value: String(y), label: String(y) }));

const buildSchema = (isEditing: boolean, isServiceUser: boolean) =>
  Yup.object({
    tax_year: Yup.string().required("Required"),
    quarter: Yup.string().required("Required"),
    amount: Yup.number()
      .positive("Amount must be positive")
      .required("Required"),
    payment_date: Yup.string().required("Required"),
    payment_method: Yup.string().required("Required"),
    confirmation_number: Yup.string().optional(),
    receipt:
      !isEditing && !isServiceUser
        ? Yup.mixed().required("Receipt is required")
        : Yup.mixed().optional(),
    notes: Yup.string().optional(),
  });

type Props = ModalProps & {
  entityId: string;
  year: string;
  payment?: IRSPayment;
};

export const LogPaymentModal = ({
  isOpen,
  close,
  entityId,
  year,
  payment,
}: Props) => {
  const { alertToast, successToast } = useToast();
  const { uuid: groupId, unsorted_folder_id } = useCurrentGroupContext();
  const { isAdmin, isCpa } = useRoleBasedView();
  const isServiceUser = isAdmin || isCpa;
  const [logPayment, { isLoading: isLogging }] = useLogIRSPaymentMutation();
  const [updatePayment, { isLoading: isUpdating }] =
    useUpdateIRSPaymentMutation();
  const [uploadFile, { isLoading: isUploading }] = useUploadFileMutation();

  const isEditing = !!payment;
  const isReceiptRequired = !isEditing && !isServiceUser;
  const validationSchema = useMemo(
    () => buildSchema(isEditing, isServiceUser),
    [isEditing, isServiceUser]
  );

  const handleSubmit = async (values: FormikValues) => {
    try {
      let file_id: string | undefined;

      if (values.receipt instanceof File) {
        const uploaded = await uploadFile({
          files: [values.receipt],
          groupId,
          parentFolderId: unsorted_folder_id,
        }).unwrap();
        file_id = uploaded[0]?.uuid;
      }

      if (isEditing) {
        await updatePayment({
          entityId,
          paymentId: payment.id,
          payload: {
            year: values.tax_year,
            quarter: values.quarter,
            amount: Number(values.amount),
            payment_date: values.payment_date,
            payment_method: values.payment_method,
            confirmation_number: values.confirmation_number || undefined,
            file_id,
            notes: values.notes || undefined,
          },
        }).unwrap();
        successToast({ message: "Payment updated" });
      } else {
        await logPayment({
          entityId,
          payload: {
            year: values.tax_year,
            quarter: values.quarter,
            amount: Number(values.amount),
            payment_date: values.payment_date,
            payment_method: values.payment_method,
            confirmation_number: values.confirmation_number || undefined,
            file_id,
            notes: values.notes || undefined,
          },
        }).unwrap();
        successToast({ message: "IRS payment logged successfully" });
      }

      close();
    } catch (e) {
      alertToast({
        message:
          (e as BackendError)?.data?.error?.message ||
          (isEditing ? "Failed to update payment" : "Failed to log payment"),
      });
    }
  };

  const isBusy = isLogging || isUpdating || isUploading;

  const initialValues = isEditing
    ? {
        tax_year: payment.year,
        quarter: payment.quarter ?? "",
        amount: String(payment.amount),
        payment_date: payment.payment_date,
        payment_method: payment.payment_method,
        confirmation_number: payment.confirmation_number ?? "",
        receipt: null,
        notes: payment.notes ?? "",
      }
    : {
        tax_year: year,
        quarter: "",
        amount: "",
        payment_date: "",
        payment_method: "",
        confirmation_number: "",
        receipt: null,
        notes: "",
      };

  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Modal.Content useCustomOverlay>
        <Modal.Header>
          <div className="t-flex t-flex-col t-gap-1">
            <Modal.Title>
              {isEditing ? "Edit Payment" : "Log IRS Payment"}
            </Modal.Title>
            {!isEditing && (
              <Modal.Subtitle>
                Record an estimated tax payment made to the IRS.
              </Modal.Subtitle>
            )}
          </div>
          <Modal.Close onClick={close} />
        </Modal.Header>
        <FormikForm
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ setFieldValue }) => (
            <>
              <Modal.Body className="t-space-y-4">
                <div className="t-grid t-grid-cols-2 t-gap-4">
                  <Field name="tax_year">
                    {({ field, form }: FieldProps) => (
                      <Combobox
                        label="Tax Year"
                        required
                        options={YEAR_OPTIONS}
                        value={
                          YEAR_OPTIONS.find((o) => o.value === field.value) ??
                          null
                        }
                        onChange={(option) =>
                          form.setFieldValue(
                            "tax_year",
                            (option as { value: string } | null)?.value ?? ""
                          )
                        }
                        isClearable={false}
                        menuPortalTarget={document.body}
                      />
                    )}
                  </Field>
                  <PriceInput
                    name="amount"
                    label="Amount"
                    required
                    placeholder="0.00"
                  />
                </div>

                <div className="t-grid t-grid-cols-2 t-gap-4">
                  <Field name="payment_date">
                    {({ field }: FieldProps) => (
                      <DateInput
                        {...field}
                        label="Payment Date"
                        required
                        saveFormat="YYYY-MM-DD"
                        portalId="log-payment-date"
                      />
                    )}
                  </Field>
                  <Field name="quarter">
                    {({ field, form }: FieldProps) => (
                      <Combobox
                        label="Quarter"
                        required
                        options={QUARTER_OPTIONS}
                        value={
                          QUARTER_OPTIONS.find(
                            (o) => o.value === field.value
                          ) ?? null
                        }
                        onChange={(option) =>
                          form.setFieldValue(
                            "quarter",
                            (option as { value: string } | null)?.value ?? ""
                          )
                        }
                        isClearable={false}
                        menuPortalTarget={document.body}
                      />
                    )}
                  </Field>
                </div>

                <Field name="payment_method">
                  {({ field, form }: FieldProps) => (
                    <Combobox
                      label="Payment Method"
                      required
                      options={PAYMENT_METHOD_OPTIONS}
                      value={
                        PAYMENT_METHOD_OPTIONS.find(
                          (o) => o.value === field.value
                        ) ?? null
                      }
                      onChange={(option) =>
                        form.setFieldValue(
                          "payment_method",
                          (option as { value: string } | null)?.value ?? ""
                        )
                      }
                      isClearable={false}
                      menuPortalTarget={document.body}
                    />
                  )}
                </Field>

                <TextInput
                  name="confirmation_number"
                  label="Confirmation #"
                  placeholder="e.g. 1234567890"
                />

                <div>
                  <label className="t-text-body-sm t-font-medium t-text-text-60 t-mb-1 t-block">
                    Upload Receipt{" "}
                    {isReceiptRequired ? (
                      <span className="t-text-red-50">*</span>
                    ) : (
                      <span className="t-text-text-30 t-font-normal">
                        (Optional)
                      </span>
                    )}
                  </label>
                  {isEditing && payment.receipt_url && (
                    <p className="t-text-body-sm t-text-text-30 t-mb-2">
                      Current:{" "}
                      <Button
                        type="button"
                        customType="link"
                        onClick={() =>
                          window.open(payment.receipt_url!, "_blank")
                        }
                      >
                        View receipt
                      </Button>
                    </p>
                  )}
                  <FileInput
                    withForm
                    name="receipt"
                    isUploading={isUploading}
                    onDelete={() => setFieldValue("receipt", null)}
                    accept={{ "application/pdf": [], "image/*": [] }}
                    label="Drag and drop or click to upload (PDF, JPG, PNG)"
                  />
                </div>

                <TextArea
                  name="notes"
                  label="Notes"
                  rows={2}
                  placeholder="Optional notes"
                  block
                />
              </Modal.Body>

              <Modal.FooterButtonGroup>
                <Modal.RawClose asChild>
                  <Button disabled={isBusy}>Cancel</Button>
                </Modal.RawClose>
                <Button
                  customType="primary"
                  type="submit"
                  isLoading={isBusy}
                  disabled={isBusy}
                >
                  {isEditing ? "Save" : "Save Payment"}
                </Button>
              </Modal.FooterButtonGroup>
            </>
          )}
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};
